/**
 * Tiny typed wrapper around localStorage. Silently no-ops in environments
 * where localStorage is unavailable (private mode, SSR) so callers don't
 * need try/catch blocks.
 */

export function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeJSON<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded or storage disabled — ignore */
  }
}

export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Run `fn` when another tab in this browser writes `key`.
 *
 * The `storage` event never fires in the tab that did the writing, so this is
 * purely a cross-tab signal — no echo, no loop. Without it two tabs each hold
 * their own in-memory snapshot and the last one to write wins, silently
 * discarding the other's answers.
 */
export function onExternalChange(key: string, fn: () => void): void {
  if (typeof window === "undefined") return;
  window.addEventListener("storage", (event) => {
    if (event.key === key) fn();
  });
}

/**
 * Namespace prefix for all keys this app writes. Makes it easy to scope a
 * "reset all my progress" action in the future.
 */
export const NS = "lex30";

export function k(name: string): string {
  return `${NS}:${name}`;
}
